import { useState, useMemo } from 'react';
import { useLocalProyectos } from '../../../hooks/useLocalProyectos';
import ProyectoCardDetalle from './ProyectoCardDetalle';

const AREAS = ['Todas las áreas', 'Ciencias de la Salud', 'Ingeniería y Tecnología', 'Ciencias Sociales', 'Educación', 'Humanidades', 'Ciencias Exactas y Naturales', 'Ciencias Económicas y Administrativas', 'Arte y Diseño'];
const ESTADOS = ['Todos los estados', 'Pendiente evaluación', 'En evaluación', 'Con correcciones', 'Aprobado', 'No pertinente'];

export default function AsignacionCodigo() {
  const { proyectos } = useLocalProyectos();
  const [busqueda, setBusqueda] = useState('');
  const [filtroArea, setFiltroArea] = useState('Todas las áreas');
  const [filtroEstado, setFiltroEstado] = useState('Todos los estados');

  const stats = useMemo(() => ({
    total: proyectos.length,
    pendiente: proyectos.filter(p => p.estado === 'Pendiente evaluación').length,
    enEvaluacion: proyectos.filter(p => p.estado === 'En evaluación').length,
    conDoc: proyectos.filter(p => !!p.documentoNombre).length,
  }), [proyectos]);

  const proyectosFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase();
    return proyectos.filter(p => {
      const matchBusqueda = !q || p.codigo.toLowerCase().includes(q) || p.titulo.toLowerCase().includes(q) || p.investigador.toLowerCase().includes(q);
      const matchArea = filtroArea === 'Todas las áreas' || p.area === filtroArea;
      const matchEstado = filtroEstado === 'Todos los estados' || p.estado === filtroEstado;
      return matchBusqueda && matchArea && matchEstado;
    });
  }, [proyectos, busqueda, filtroArea, filtroEstado]);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
            <i className="ri-price-tag-3-line text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Código y Categoría de Proyectos</h2>
        </div>
        <p className="text-slate-500 text-sm ml-11">
          El código es asignado automáticamente al momento del envío. Aquí puede revisar todos los proyectos recibidos, sus detalles completos y descargar los documentos adjuntos.
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
        <div className="w-5 h-5 flex items-center justify-center mt-0.5">
          <i className="ri-information-line text-teal-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-teal-800 mb-0.5">Asignación automática de códigos</p>
          <p className="text-xs text-teal-700">
            Cuando el investigador envía su propuesta, el sistema genera automáticamente un código único con prefijo según el tipo de trabajo
            (<span className="font-mono">PRY</span> institucional, <span className="font-mono">TES</span> tesis, <span className="font-mono">DOC</span> docente, <span className="font-mono">CON</span> convenio).
            El investigador recibe ese código al instante para el seguimiento.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total de proyectos', value: stats.total, icon: 'ri-file-list-3-line', color: 'text-teal-600 bg-teal-50' },
          { label: 'Pendientes', value: stats.pendiente, icon: 'ri-time-line', color: 'text-amber-600 bg-amber-50' },
          { label: 'En evaluación', value: stats.enEvaluacion, icon: 'ri-search-eye-line', color: 'text-sky-600 bg-sky-50' },
          { label: 'Con documento adjunto', value: stats.conDoc, icon: 'ri-attachment-2', color: 'text-violet-600 bg-violet-50' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${s.color}`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{s.value}</p>
              <p className="text-xs text-slate-500 leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por código, título o investigador..."
            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
          />
        </div>
        <select
          value={filtroArea}
          onChange={e => setFiltroArea(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white cursor-pointer"
        >
          {AREAS.map(a => <option key={a}>{a}</option>)}
        </select>
        <select
          value={filtroEstado}
          onChange={e => setFiltroEstado(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white cursor-pointer"
        >
          {ESTADOS.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          Mostrando <strong>{proyectosFiltrados.length}</strong> de <strong>{proyectos.length}</strong> proyectos
        </p>
        {(busqueda || filtroArea !== 'Todas las áreas' || filtroEstado !== 'Todos los estados') && (
          <button
            onClick={() => { setBusqueda(''); setFiltroArea('Todas las áreas'); setFiltroEstado('Todos los estados'); }}
            className="whitespace-nowrap text-xs text-teal-600 hover:underline cursor-pointer"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Project list */}
      {proyectosFiltrados.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-inbox-2-line text-4xl" />
          </div>
          <p className="text-sm font-medium">No se encontraron proyectos</p>
          <p className="text-xs mt-1">Ajuste los filtros o espere a que lleguen nuevas propuestas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proyectosFiltrados.map(p => (
            <ProyectoCardDetalle key={p.codigo} proyecto={p} />
          ))}
        </div>
      )}
    </div>
  );
}
